import React, { useState, useEffect } from "react";
import { settingsService } from "../services/dataService";
import { Settings as SettingsType } from "../types";
import { useSettings } from "../context/SettingsContext";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import "./Settings.css";

const Settings: React.FC = () => {
  const { refreshSettings } = useSettings();
  const [currentSettings, setCurrentSettings] = useState<SettingsType | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    settingsName: "Varsayılan Ayarlar",
    profitMargin: 40,
    overheadCostRate: 40,
    showStockManagement: false,
  });
  const [formErrors, setFormErrors] = useState<{
    profitMargin?: string;
    overheadCostRate?: string;
  }>({});

  // Alert Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await settingsService.getDefault();
      setCurrentSettings(data);
      setFormData({
        settingsName: data.settingsName,
        profitMargin: data.profitMargin,
        overheadCostRate: data.overheadCostRate,
        showStockManagement: data.showStockManagement ?? false,
      });
    } catch (error) {
      console.error("❌ Ayarlar yüklenirken hata:", error);
      setCurrentSettings(null);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: typeof formErrors = {};

    if (formData.profitMargin < 0 || formData.profitMargin > 100) {
      errors.profitMargin = "Kar marjı 0-100 arasında olmalıdır";
    }

    if (formData.overheadCostRate < 0 || formData.overheadCostRate > 100) {
      errors.overheadCostRate = "Genel gider oranı 0-100 arasında olmalıdır";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      if (currentSettings) {
        await settingsService.update(currentSettings.settingsId, {
          ...currentSettings,
          ...formData,
        });
        setAlertModal({
          isOpen: true,
          message: "Ayarlar başarıyla güncellendi.",
          type: "success",
        });
      } else {
        await settingsService.create(formData as any);
        setAlertModal({
          isOpen: true,
          message: "Ayarlar başarıyla oluşturuldu.",
          type: "success",
        });
      }
      await loadSettings();
      await refreshSettings();
    } catch (error: any) {
      console.error("❌ Ayarlar kaydedilirken hata:", error);
      setAlertModal({
        isOpen: true,
        message:
          error.response?.data || "Ayarlar kaydedilirken bir hata oluştu.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // Hesaplama değerleri
  const baseCost = 1000;
  const overheadAmount = (baseCost * formData.overheadCostRate) / 100;
  const subtotal = baseCost + overheadAmount;
  const profitAmount = (subtotal * formData.profitMargin) / 100;
  const totalPrice = subtotal + profitAmount;

  if (loading) {
    return <PageLoader message="Ayarlar yükleniyor..." />;
  }

  return (
    <div className="settings-container">
      {/* Page Header */}
      <div className="settings-header">
        <div className="header-content">
          <div className="header-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="3"></circle>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
            </svg>
          </div>
          <div className="header-text">
            <h1>Sistem Ayarları</h1>
            <p className="subtitle">
              Fiyatlandırma parametrelerini ve modül görünürlüklerini yönetin
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="settings-content">
          {/* Left Column - Settings Cards */}
          <div className="settings-cards">
            {/* Pricing Settings Card */}
            <div className="settings-card">
              <div className="card-header">
                <div className="card-icon pricing">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="12" y1="1" x2="12" y2="23"></line>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                </div>
                <div className="card-title-group">
                  <h3>Fiyatlandırma Ayarları</h3>
                  <p>Kar marjı ve genel gider oranlarını belirleyin</p>
                </div>
              </div>
              <div className="card-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>
                      Kar Marjı <span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={formData.profitMargin}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            profitMargin: parseFloat(e.target.value) || 0,
                          })
                        }
                        className={formErrors.profitMargin ? "error" : ""}
                      />
                      <span className="input-suffix">%</span>
                    </div>
                    {formErrors.profitMargin && (
                      <span className="error-message">
                        {formErrors.profitMargin}
                      </span>
                    )}
                    <span className="help-text">
                      Maliyet üzerine eklenecek kar oranı
                    </span>
                  </div>

                  <div className="form-group">
                    <label>
                      Genel Gider Oranı <span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={formData.overheadCostRate}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            overheadCostRate: parseFloat(e.target.value) || 0,
                          })
                        }
                        className={formErrors.overheadCostRate ? "error" : ""}
                      />
                      <span className="input-suffix">%</span>
                    </div>
                    {formErrors.overheadCostRate && (
                      <span className="error-message">
                        {formErrors.overheadCostRate}
                      </span>
                    )}
                    <span className="help-text">
                      Genel giderler için eklenecek oran
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Module Settings Card */}
            <div className="settings-card">
              <div className="card-header">
                <div className="card-icon modules">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="3" width="7" height="7"></rect>
                    <rect x="14" y="3" width="7" height="7"></rect>
                    <rect x="14" y="14" width="7" height="7"></rect>
                    <rect x="3" y="14" width="7" height="7"></rect>
                  </svg>
                </div>
                <div className="card-title-group">
                  <h3>Modül Ayarları</h3>
                  <p>Sistemde hangi modüllerin görüneceğini belirleyin</p>
                </div>
              </div>
              <div className="card-body">
                <div
                  className={`toggle-group ${formData.showStockManagement ? "active" : ""}`}
                >
                  <div className="toggle-info">
                    <div className="toggle-icon">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                        <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                        <line x1="12" y1="22.08" x2="12" y2="12"></line>
                      </svg>
                    </div>
                    <div className="toggle-text">
                      <h4>Stok Yönetim Modülü</h4>
                      <p>Stok takibi, hareketler ve ürün ağacı yönetimi</p>
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={formData.showStockManagement}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          showStockManagement: e.target.checked,
                        })
                      }
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              {currentSettings && (
                <div className="last-update">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  <span>
                    Son güncelleme:{" "}
                    {currentSettings.updatedAt
                      ? new Date(currentSettings.updatedAt).toLocaleString(
                          "tr-TR",
                        )
                      : new Date(currentSettings.createdAt).toLocaleString(
                          "tr-TR",
                        )}
                  </span>
                </div>
              )}
              <button type="submit" className="btn-save" disabled={saving}>
                {saving ? (
                  <>
                    <span className="spinner"></span>
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                      <polyline points="17 21 17 13 7 13 7 21"></polyline>
                      <polyline points="7 3 7 8 15 8"></polyline>
                    </svg>
                    Değişiklikleri Kaydet
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Right Column - Calculator Preview */}
          <div className="calculator-card">
            <div className="calculator-header">
              <h3>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect>
                  <line x1="8" y1="6" x2="16" y2="6"></line>
                  <line x1="8" y1="10" x2="16" y2="10"></line>
                  <line x1="8" y1="14" x2="12" y2="14"></line>
                  <line x1="8" y1="18" x2="12" y2="18"></line>
                </svg>
                Canlı Hesaplama Önizlemesi
              </h3>
              <p>1.000 TL maliyet üzerinden örnek hesaplama</p>
            </div>
            <div className="calculator-body">
              <div className="calc-row">
                <span className="calc-label">Temel Maliyet</span>
                <span className="calc-value">
                  {baseCost.toLocaleString("tr-TR")} ₺
                </span>
              </div>
              <div className="calc-row">
                <span className="calc-label">
                  Genel Gider
                  <span className="badge overhead">
                    %{formData.overheadCostRate}
                  </span>
                </span>
                <span className="calc-value">
                  +{overheadAmount.toLocaleString("tr-TR")} ₺
                </span>
              </div>
              <div className="calc-row">
                <span className="calc-label">Ara Toplam</span>
                <span className="calc-value">
                  {subtotal.toLocaleString("tr-TR")} ₺
                </span>
              </div>
              <div className="calc-row">
                <span className="calc-label">
                  Kar Marjı
                  <span className="badge profit">%{formData.profitMargin}</span>
                </span>
                <span className="calc-value">
                  +{profitAmount.toLocaleString("tr-TR")} ₺
                </span>
              </div>
              <div className="calc-row highlight">
                <span className="calc-label">Satış Fiyatı</span>
                <span className="calc-value">
                  {totalPrice.toLocaleString("tr-TR")} ₺
                </span>
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />
    </div>
  );
};

export default Settings;
